import { useFormikContext } from "formik";
import React from "react";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import PlusIcon from "../../../../assets/svg/icons/common/Plus";
import DeleteIcon from "../../../../assets/svg/icons/common/delete";
import CheckGreenCircleIcon from "../../../../assets/svg/icons/common/check-green-circle";
import FancyCheckBox from "../../../../components/form/fancy-checkbox";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import Switch from "../../../../components/form/switch";
import TextInput from "../../../../components/form/text-input";
import Button from "../../../../components/new-button";
import SlidingToggle from "../../../../components/form/sliding-checkbox";
import useUserStore from "../../../../stores/user";
import useDomainStore from "../../../../stores/domain";
import DualPricing from "./dual-pricing";
import FeeDefaults from "./fee-defaults";
import FlatRate from "./flat-rate";
import KurvPricing from "./kurv-pricing";
import IcPlus from "./ic-plus";
import styles from "./style.module.scss";
import Surcharge from "./surcharge";
import Tiered from "./tiered";
import baseStyles from "../../style.module.scss";
import ACHPricing from "./ach";
import { createPricingInfoSchema } from "../../validation-schema";
import useOrganizationStore from "../../../../stores/organization";
import { getSoftLimitWarning } from "../../utils/soft-limit";
import {
  getAllDefaultPricingModelValues,
  pricingModelRequiresDailyBilling,
} from "../../utils/pricing-defaults";
import {
  withKurvTierRules,
  addAchPricingPatch,
  removeAchPricingPatch,
  addCCPricingPatch,
  removeCCPricingPatch,
} from "../../utils/processor-pricing-rules";

const pricingModels = {
  dual_pricing: "Dual Pricing",
  surcharge: "Surcharge",
  flat_rate: "Flat Rate",
  kurv_pricing: "Kurv Pricing",
  tiered: "Tiered",
  ic_plus: "IC Plus",
  ach: "ACH",
};

const Pricing = ({ isOpen, onToggle, showPaylianceIndustry = false }) => {
  const { isKurvDomain } = useDomainStore();
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
  } = useFormikContext();
  const user = useUserStore((state) => state.user);

  const nonEditableTemplateOrPricingRestricted =
    values.nonEditableTemplateApplied ||
    user?.organization?.pricing_restricted === 1;

  const isSectionValid = createPricingInfoSchema(pricingDefaults, {
    requirePaylianceIndustry: showPaylianceIndustry,
  }).isValidSync(values);

  const handleAddACHProcessing = () => {
    setValues((prev) => {
      const patch = addAchPricingPatch({
        pricingModel: prev.pricing_model,
        pricingDefaults,
        processors: prev.processors,
      });
      return {
        ...prev,
        ...patch,
        ...withKurvTierRules({
          values: prev,
          nextProcessors: patch.processors,
          pricingDefaults,
          patch,
        }),
      };
    });
  };

  const handlePricingModelChange = (newPricingModel) => {
    setValues((prev) => {
      const patch = {
        pricing_model: newPricingModel,
        ...getAllDefaultPricingModelValues(pricingDefaults),
        ...(pricingModelRequiresDailyBilling(newPricingModel)
          ? { billing_sequence: "daily" }
          : {}),
        setup_fee: pricingDefaults?.[newPricingModel]?.setup_fee?.default,
        monthly_access_fee:
          pricingDefaults?.[newPricingModel]?.monthly_access_fee?.default,
        data_breach: pricingDefaults?.[newPricingModel]?.data_breach?.default,
        monthly_minimum:
          pricingDefaults?.[newPricingModel]?.monthly_minimum?.default,
        semi_annual_tech_fee:
          pricingDefaults?.[newPricingModel]?.semi_annual_tech_fee?.default,
        voice_aru: pricingDefaults?.[newPricingModel]?.voice_aru?.default,
        batch_fee: pricingDefaults?.[newPricingModel]?.batch_fee?.default,
        ebt_transactions:
          pricingDefaults?.[newPricingModel]?.ebt_transactions?.default,
        avs_fee: pricingDefaults?.[newPricingModel]?.avs_fee?.default,
        auth_transaction:
          pricingDefaults?.[newPricingModel]?.auth_transaction?.default,
        etf_fee: pricingDefaults?.[newPricingModel]?.etf_fee?.default,
        chargeback_fee:
          pricingDefaults?.[newPricingModel]?.chargeback_fee?.default,
        representment_fee:
          pricingDefaults?.[newPricingModel]?.representment_fee?.default,
        pre_dispute_fee:
          pricingDefaults?.[newPricingModel]?.pre_dispute_fee?.default,
        returned_ach_item:
          pricingDefaults?.[newPricingModel]?.returned_ach_item?.default,
        tiered_amex_structure:
          newPricingModel == "surcharge"
            ? "ic_plus"
            : newPricingModel == "tiered"
              ? "tiered"
              : "flat_rate",
        card_brand_fee:
          pricingDefaults?.[newPricingModel]?.card_brand_fee?.default ?? 0,
        pci_non_compliance_fee:
          pricingDefaults?.[newPricingModel]?.pci_non_compliance_fee?.default ??
          0,
        retrieval_fee:
          pricingDefaults?.[newPricingModel]?.retrieval_fee?.default ?? 0,
      };

      return {
        ...prev,
        ...patch,
        ...withKurvTierRules({
          values: prev,
          nextProcessors: prev.processors,
          pricingDefaults,
          patch,
        }),
      };
    });
  };

  const handleAddCreditCardProcessing = () => {
    setValues((prev) => {
      const patch = addCCPricingPatch({
        pricingModel: prev.pricing_model,
        pricingDefaults,
      });
      return {
        ...prev,
        ...patch,
        ...withKurvTierRules({
          values: prev,
          nextProcessors: prev.processors,
          pricingDefaults,
          patch,
        }),
      };
    });
  };

  const handleRemoveCreditCardProcessing = () => {
    setValues((prev) => {
      const patch = removeCCPricingPatch({
        addAch: prev.add_ach,
        pricingDefaults,
        processors: prev.processors,
      });
      return {
        ...prev,
        ...patch,
        ...withKurvTierRules({
          values: prev,
          nextProcessors: patch.processors ?? prev.processors,
          pricingDefaults,
          patch,
        }),
      };
    });
  };

  const handleRemoveACHProcessing = () => {
    setValues((prev) => {
      const patch = removeAchPricingPatch({
        pricingModel: prev.pricing_model,
        pricingDefaults,
        processors: prev.processors,
      });
      return {
        ...prev,
        ...patch,
        ...withKurvTierRules({
          values: prev,
          nextProcessors: patch.processors ?? prev.processors,
          pricingDefaults,
          patch,
        }),
      };
    });
  };

  const showCreditCardProcessing =
    values?.pricing_model?.length > 0 && values.pricing_model !== "ach";
  const showACHProcessing =
    values.add_ach === "yes" || values.pricing_model === "ach";

  const activePricingModelsExceptACH = pricingDefaults?.active_pricing_models
    ? Object.keys(pricingModels)
        .filter((key) => key !== "ach")
        .filter((key) => pricingDefaults.active_pricing_models[key] === 1)
    : Object.keys(pricingModels).filter((key) => key !== "ach");

  const isACHActive = pricingDefaults?.active_pricing_models
    ? pricingDefaults.active_pricing_models.ach === 1
    : true;

  return (
    <div className={baseStyles.section}>
      <div className={baseStyles.sectionHeader} onClick={onToggle}>
        <div
          className={`${baseStyles.sectionHeaderLeft} ${isOpen && baseStyles.open}`}
        >
          <CaretDown24 />
          <h6 className={baseStyles.sectionTitle}>Pricing</h6>
        </div>
        <div className={baseStyles.sectionHeaderRight}>
          <div className={baseStyles.sectionHeaderRight}>
            {isSectionValid && <CheckGreenCircleIcon />}
          </div>
        </div>
      </div>

      {isOpen && (
        <>
          <div
            className={`${styles.card} ${styles.cardInner} ${
              !showCreditCardProcessing && styles.cardPadding2
            }`}
            id="pricing_model"
          >
            <div className={`${styles.content}`}>
              <div className={styles.paymentMethodHeader}>
                <h6 className={styles.card_heading}>CREDIT CARD PROCESSING</h6>
                <div className={styles.paymentMethodButtons}>
                  <Button
                    type="button"
                    icon={
                      showCreditCardProcessing ? (
                        <DeleteIcon />
                      ) : (
                        <PlusIcon
                          color={isKurvDomain ? "#22A276" : "#4C70EF"}
                        />
                      )
                    }
                    variant="ghost"
                    size="small"
                    onClick={
                      !showCreditCardProcessing
                        ? handleAddCreditCardProcessing
                        : handleRemoveCreditCardProcessing
                    }
                    disabled={nonEditableTemplateOrPricingRestricted}
                  >
                    {!showCreditCardProcessing && (
                      <span>
                        Add <span className={styles.hide}>method</span>
                      </span>
                    )}
                  </Button>
                </div>
              </div>
              {showCreditCardProcessing && (
                <div className={styles.content}>
                  <div className={styles.pricingButtons}>
                    {activePricingModelsExceptACH.map((model) => (
                      <FancyRadioInput
                        className={styles.pricingButton}
                        key={`model-${model}`}
                        id={model}
                        name="pricing_model"
                        label={pricingModels[model]}
                        value={model}
                        checked={values.pricing_model == model}
                        onChange={(e) => {
                          handlePricingModelChange(e.target.value);
                          handleBlur(e);
                        }}
                        onBlur={handleBlur}
                        disabled={nonEditableTemplateOrPricingRestricted}
                      />
                    ))}
                  </div>

                  {errors?.pricing_model && touched?.pricing_model && (
                    <p className={styles.error}>{errors?.pricing_model}</p>
                  )}

                  {user?.organization?.ems_entity === "ems" && (
                    <div className={styles.radioItem}>
                      <p
                        className={styles.p_heading}
                        style={{ marginBottom: "12px" }}
                      >
                        Pricing pre-approved by Rate Desk?
                      </p>
                      <div className={styles.radios}>
                        <SlidingToggle
                          options={["yes", "no"]}
                          width="150px"
                          value={values.pricing_approved_by_rate_desk}
                          onChange={(values) =>
                            handleChange({
                              target: {
                                name: "pricing_approved_by_rate_desk",
                                value: values,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {values.pricing_model && (
                    <div className={styles.pricingSection}>
                      {values.pricing_model == "dual_pricing" && (
                        <DualPricing />
                      )}
                      {values.pricing_model == "surcharge" && <Surcharge />}
                      {values.pricing_model == "flat_rate" && <FlatRate />}
                      {values.pricing_model == "kurv_pricing" && (
                        <KurvPricing />
                      )}
                      {values.pricing_model == "ic_plus" && <IcPlus />}
                      {values.pricing_model == "tiered" && <Tiered />}
                    </div>
                  )}

                  <div className={styles.card}>
                    <h6 className={styles.card_heading}>
                      EBT/FNS Transactions
                    </h6>
                    <div className={styles.card_body}>
                      <Switch
                        id="ebt"
                        name="ebt"
                        label="EBT"
                        checked={values.ebt === "yes"}
                        onChange={(e) => {
                          handleChange(e);
                          setFieldValue("ebt", e.target.checked ? "yes" : "no");
                        }}
                        onBlur={(e) => {
                          handleBlur(e);
                          setFieldValue("ebt", e.target.checked ? "yes" : "no");
                        }}
                        disabled={nonEditableTemplateOrPricingRestricted}
                      />
                      {values.ebt === "yes" && (
                        <div
                          className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                        >
                          <TextInput
                            label={"FNS Number"}
                            placeholder={"FNS Number"}
                            type="text"
                            inputMode="decimal"
                            id="fnf_number"
                            name="fnf_number"
                            value={values?.fnf_number}
                            onBlur={handleBlur}
                            touched={touched?.fnf_number}
                            errored={errors?.fnf_number}
                            error={errors?.fnf_number}
                            disabled={
                              !values.ebt ||
                              nonEditableTemplateOrPricingRestricted
                            }
                            cleaveOptions={{
                              numericOnly: true,
                              blocks: [15],
                              onValueChanged: (e) => {
                                setFieldValue("fnf_number", e.target.value);
                              },
                            }}
                          />
                          <TextInput
                            label={"EBT Transactions"}
                            placeholder={"0.00"}
                            type="text"
                            prefix={"$"}
                            suffix={"/txn"}
                            id="ebt_transactions"
                            name="ebt_transactions"
                            value={values?.ebt_transactions}
                            onBlur={handleBlur}
                            touched={touched?.ebt_transactions}
                            errored={errors?.ebt_transactions}
                            error={errors?.ebt_transactions}
                            hasWarning={getSoftLimitWarning(
                              pricingDefaults?.[values.pricing_model]
                                ?.ebt_transactions?.soft_limit,
                              values.ebt_transactions,
                              errors.ebt_transactions,
                            )}
                            disabled={
                              !values.ebt ||
                              nonEditableTemplateOrPricingRestricted
                            }
                            cleaveOptions={{
                              numeral: true,
                              numeralDecimalScale: 2,
                              numeralIntegerScale: 4,
                              numeralPositiveOnly: true,
                              onValueChanged: (e) => {
                                handleChange({
                                  target: {
                                    name: "ebt_transactions",
                                    value: e.target.rawValue,
                                  },
                                });
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <FeeDefaults />
                </div>
              )}
            </div>
          </div>

          {isACHActive && (
            <div
              className={`${styles.card} ${styles.cardInner} ${
                !showACHProcessing && styles.cardPadding2
              }`}
            >
              <div className={`${styles.content}`}>
                <div className={styles.paymentMethodHeader}>
                  <h6 className={styles.card_heading}>ACH</h6>
                  <div className={styles.paymentMethodButtons}>
                    <Button
                      type="button"
                      icon={
                        showACHProcessing ? (
                          <DeleteIcon />
                        ) : (
                          <PlusIcon
                            color={isKurvDomain ? "#22A276" : "#4C70EF"}
                          />
                        )
                      }
                      variant="ghost"
                      size="small"
                      onClick={
                        !showACHProcessing
                          ? handleAddACHProcessing
                          : handleRemoveACHProcessing
                      }
                      disabled={nonEditableTemplateOrPricingRestricted}
                    >
                      {!showACHProcessing && (
                        <span>
                          Add <span className={styles.hide}>method</span>
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
                {showACHProcessing && (
                  <div className={styles.content}>
                    <ACHPricing showPaylianceIndustry={showPaylianceIndustry} />
                  </div>
                )}
              </div>
            </div>
          )}

          {errors.pricing_model && touched.pricing_model && (
            <div className={styles.content}>
              <p className={styles.error}>{errors.pricing_model}</p>
            </div>
          )}

          <div className={`${styles.card} ${styles.cardInner}`}>
            <div className={styles.content}>
              <h6 className={styles.card_heading}>VAR sheet</h6>
              <div className={styles.card_body}>
                <Switch
                  id="var_sheet_request"
                  name="var_sheet_request"
                  label="Request VAR sheet only"
                  checked={values.var_sheet_request || false}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={nonEditableTemplateOrPricingRestricted}
                />

                <p className={styles.p_heading}>
                  Are you already using a gateway or terminal?
                </p>

                <div
                  className={`${styles.flex} ${styles.flexWrap} ${styles.gap1}`}
                >
                  <FancyCheckBox
                    id="Gateway"
                    name="Gateway"
                    label="Gateway"
                    checked={values?.var_only?.type == "gateway"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue("var_only", {
                          type: "gateway",
                          model: "",
                        });
                      } else {
                        setFieldValue("var_only", {});
                      }
                    }}
                    disabled={nonEditableTemplateOrPricingRestricted}
                  />
                  <FancyCheckBox
                    id="Terminal"
                    name="Terminal"
                    label="Terminal"
                    checked={values?.var_only?.type == "terminal"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue("var_only", {
                          type: "terminal",
                          model: "",
                          serial: "",
                        });
                      } else {
                        setFieldValue("var_only", {});
                      }
                    }}
                    disabled={nonEditableTemplateOrPricingRestricted}
                  />
                </div>

                {values?.var_only?.type == "gateway" && (
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                  >
                    <TextInput
                      label={"Gateway name"}
                      placeholder={"Gateway name"}
                      type="text"
                      id="var_only.model"
                      name="var_only.model"
                      value={values?.var_only?.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.model}
                      errored={errors?.var_only?.model}
                      error={errors?.var_only?.model}
                      disabled={nonEditableTemplateOrPricingRestricted}
                    />
                  </div>
                )}

                {values?.var_only?.type == "terminal" && (
                  <div
                    className={`${styles.grid} ${styles.gridCols2} ${styles.gap1}`}
                  >
                    <TextInput
                      label={"Model"}
                      placeholder={"Model"}
                      type="text"
                      id="var_only.model"
                      name="var_only.model"
                      value={values?.var_only?.model}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.model}
                      errored={errors?.var_only?.model}
                      error={errors?.var_only?.model}
                      disabled={nonEditableTemplateOrPricingRestricted}
                    />

                    <TextInput
                      label={"Serial Number"}
                      placeholder={"Serial Number"}
                      type="text"
                      id="var_only.serial"
                      name="var_only.serial"
                      value={values?.var_only?.serial}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched?.var_only?.serial}
                      errored={errors?.var_only?.serial}
                      error={errors?.var_only?.serial}
                      disabled={nonEditableTemplateOrPricingRestricted}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Pricing;
